import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { campaignSchema } from './campaignSchema';
import { OfferDetails, OfferSource, submitOffer } from './offerApi';

export function useOfferForm(source: OfferSource) {
  const navigate = useNavigate();
  const inFlight = useRef(false);
  const [isPending, setIsPending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const form = useForm<OfferDetails>({ resolver: zodResolver(campaignSchema) });

  const onSubmit = async (details: OfferDetails) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsPending(true);
    setSubmitError('');
    form.clearErrors();
    try {
      const result = await submitOffer(details, source);
      if (result.success) {
        form.reset();
        // Keep personal data out of the URL, browser storage and navigation state.
        navigate(`/${source}/thank-you`, { state: { offerQueued: source } });
      } else {
        setSubmitError(result.message);
        Object.entries(result.fieldErrors).forEach(([field, message], index) => {
          form.setError(field as keyof OfferDetails, { type: 'server', message }, { shouldFocus: index === 0 });
        });
      }
    } finally {
      inFlight.current = false;
      setIsPending(false);
    }
  };

  return { ...form, onSubmit, isPending, submitError };
}
